export default `# Navigation
Our movies are looking awesome. If I say so myself... Let's now take care of the \`Navigation\` part.

Again, here is our mockup:
![Sweet Pumpkins app mockup](https://sweetpumpkins.codecamps.com/sw-mockup.png)

We want to display the dropdown list with genres. To do this, we'll use the HTML tag \`select\` with options inside. The basic setup looks like this:

\`\`\`html
<select name="select">
  <option value="comedy">Comedy</option>
  <option value="action" selected>Action</option>
  <option value="drama">Drama</option>
</select>
\`\`\`

In HTML, the selected item has the \`selected\` attribute.
In React, the value of a selected option is attached as the \`value\` attribute to the \`select\` tag.

Additionally, we can decide what
happens when our selection changes by attaching the \`onChange\` event handler.

When we select a new genre, the \`onGenreChange\` method will be called.
It gets an \`event\` object from which we can extract the value of the selected genre and store it in the \`state\`.

Let's build the \`Selection\` component:

\`\`\`codeSandbox
rm6vww086p
\`\`\`

Now, we can move the \`Selection\` component to the *main/navigation/Selection.js* file, import it into the *Navigation.js*, and – finally – display it in the \`Navigation\` component.

\`\`\`codeSandbox
m45nkmjy99
\`\`\`

I also added a \`label\` tag and wrapped it in the \`div\` with class \`selection\`. In the next step, we'll create an accompanying
*Selection.css* file and add styles. Don't forget to import the *Selection.css* into the *Selection.js* file!


\`\`\`plainCss
/* Selection.css */

.selection {
  display: flex;
  flex-direction: column;
  margin-bottom: 60px;
}

.selection label {
  margin-bottom: 10px;
  color: #555;
}

.selection select {
  max-width: 150px;
}
\`\`\`

The \`label\` and \`select\` tabs should align vertically. That's why the orientation of the main axis in the \`.selection\` class is set to \`column\`.

The \`select\` element should have the maximum width of 150px. Otherwise, it would fill the whole width of the \`Navigation\` component.

## Lifting up the State

Let's take a step back and think: Which component should know about the selected genre?
If we keep information about the selected genre in the \`Selection\` component, then – because of the one-way communication in the React application –
the \`Navigation\` component will not be aware of our choice.

But there is an easy fix. We can save the selected genre in the \`Navigation\` state. We can also move the \`onGenreChange\` method to the \`Navigation\` component.
And, finally, we can pass the \`genre\` and the \`onGenreChange\` method to the \`Selection\` component in \`props\`.

I placed both the \`Navigation\` and \`Selection\` components in one file for easier visualisation. Separate them in your application!
\`\`\`codeSandbox
jlxp9wyw3
\`\`\`

The functionality of the \`Selection\` component didn't change but we moved the \`state\` one level up.

We paid a price for it – we had to pass down two additional \`props\` from the \`Navigation\` to the \`Selection\` component.

But we gained on simplicity – the \`Selection\` component became a simple functional component.

Here is our application with the \`Selection\` dropdown:

\`\`\`codeSandbox
z28w7kkk1m
\`\`\`

## Navigation Sliders

Now we can focus on the range sliders. They will let us select the ranges for the production year, rating and runtime.

Instead of building sliders from scratch, we can use a component that's already been built, by grabbing it from npm – a [react-input-range](https://www.npmjs.com/package/react-input-range).

Let's add it to the project:

\`\`\`plainJavascript
yarn add react-input-range
\`\`\`

When the installation finishes, create a new file in the *main/navigation* directory and call it *Slider.js*.

The \`InputRange\` slider accepts several parameters: *minValue*, *maxValue*, *step*, *value* and *onChange* method.

The *value* is an object with a selected range:
\`\`\`plainJavascript
{
  min: lower-range-value,
  max: higher-range-value
}
\`\`\`

We will save information about sliders in the \`state\` of the \`Navigation\` component:

\`\`\`plainJavascript
// Navigation.js

state = {
  genre: "comedy",
  year: {
    label: "year",
    min: 1990,
    max: 2017,
    step: 1,
    value: { min: 2000, max: 2017 }
  },
  rating: {
    label: "rating",
    min: 0,
    max: 10,
    step: 1,
    value: { min: 8, max: 10 }
  },
  runtime: {
    label: "runtime",
    min: 0,
    max: 300,
    step: 15,
    value: { min: 60, max: 120 }
  }
}
\`\`\`

Next, let's implement the \`Slider\` as a class component.

The \`Slider\` component will receive in \`props\`: the  \`onChange\` method and the \`data\` object from its parent – the \`Navigation\` component.

\`\`\`plainJavascript
// Slider.js

import React from "react";
import InputRange from "react-input-range";

import 'react-input-range/lib/css/index.css';
import "./Slider.css"

class Slider extends React.Component {
  onChange = range => {
    this.props.onChange({
      type: this.props.data.label,
      value: range
    });
  }

  render() {
    const { min, max, step, value, label } = this.props.data;
    return (
      <div className="slider">
        <label>{label}</label>
        <InputRange
          minValue={min}
          maxValue={max}
          step={step}
          onChange={this.onChange}
          value={value}
        />
      </div>
    )
  }
}

export default Slider;
\`\`\`

First, import the \`InputRange\` slider from the \`react-input-range\` package.

The \`Navigation\` component will pass to the \`Slider\`: \`data\` and \`onChange\` method in \`props\`.

In the \`render\` method, we destructured the \`data\` object (received in \`props\`), to get the values required by the \`InputRange\` component:

- *minValue* – the minimum value of a slider
- *maxvalue* – the maximum value of a slider
- *step* – a step of the slider – for example for \`year\` it equals \`1\`
- *onChange* is a method that will be called whenever the \`value\` of the slider changes.
- *value* – an object received in \`props\`. It contains the current minimum and maximum of the slider's range – { min, max }

The \`Slider\` component defines the \`onChange\` method. Every time when the slider range changes,
the \`onChannge\` method will be called. It will receive a range object with minimum and maximum values.

Then, the \`onChange\` method will call the \`onChange\` method received in \`props\` from the \`Navigation\` component and it will pass it a new object.

For example, for the \`year\` slider, the object that will be passed to the \`onChange\` method of the \`Navigation\` component will look like this:

\`\`\`plainJavascript
{
  type: "year",
  value: {
    min: 2010,
    max: 2017
  }
}
\`\`\`

Later, we will implement the \`onChange\` method in the \`Navigation\` component.

For now, let's style the slider. First, we have to import default styles from the sliders package:

\`\`\`plainJavascript
import 'react-input-range/lib/css/index.css';
\`\`\`

In the next step, we can fine-tune the styling.

Create a *Slider.css* file and import it into the *Slider.js*.

Documentation about the styling of the \`InputRange\` slider is scarce. The best way to find out which classes to overwrite is to inspect
the slider component in the browser.

You can leave the slider as it is or you can add the following styles to the *Sliders.css*.
\`\`\`plainCss
/* Slider.css */

.slider {
  margin-bottom: 40px;
}

.slider label {
  color: #555;
  font-size: 1rem;
  margin-bottom: 20px;
  display: block;
}

.input-range__slider {
  background: #e27a3f;
  border: none;
}

.input-range__track {
  background: #ddd;
}

.input-range__track--active {
  background: #e27a3f;
}

.input-range__label--value .input-range__label-container {
  background: #e27a3f;
  color: white;
  font-size: 0.65rem;
  padding: 2px 5px;
  border-radius: 2px;
}

.input-range__label--min .input-range__label-container,
.input-range__label--max .input-range__label-container
 {
  font-size: 0.65rem;
  color: #888;
  left: 0;
}

.input-range__label--max .input-range__label-container {
  left: 25%;
}
\`\`\`

With the slider styles sorted out, we can go back and write a \`onChange\` method in the \`Navigation\` component.

The \`onChange\` method in the \`Navigation\` component, when called by the \`onChange\` method of the \`Slider\` component, will receive an object with
\`type\` and \`data\` properties:

\`\`\`plainJavascript
{
  type: "year",
  value: {
    min: 2010,
    max: 2017
  }
}
\`\`\`

The \`data.type\` tells us which part of the state should get updated.

\`\`\`plainJavascript
// Navigation.js

onChange = data => {
  this.setState({
    [data.type]: {
      ...this.state[data.type], // previous value of this.state.year
      value: data.value         // overwrite the value property
    }
  });
};
\`\`\`

Let's assume that the \`onChange\` method has been called by the year slider.

The \`onChange\` method will set the \`year\` property on the state. It will assign to it a new object created from the previous version of \`this.state.year\`.
And it will overwrite only the \`value\` property with \`data.value\`.

Now, we can add three sliders to the \`Navigation\` component and we can pass the corresponding properties from the \`state\` and the \`onChange\` method.

Here is the full \`Navigation\` component:
\`\`\`plainJsx
// Navigation.js

import React from "react";
import "./Navigation.css";
import Selection from "./Selection";
import Slider from "./Slider";

class Navigation extends React.Component {
  state = {
    genre: "comedy",
    year: {
      label: "year",
      min: 1990,
      max: 2017,
      step: 1,
      value: { min: 2000, max: 2017 }
    },
    rating: {
      label: "rating",
      min: 0,
      max: 10,
      step: 1,
      value: { min: 8, max: 10 }
    },
    runtime: {
      label: "runtime",
      min: 0,
      max: 300,
      step: 15,
      value: { min: 60, max: 120 }
    }
  };

  onGenreChange = event => {
    this.setState({ genre: event.target.value });
  };

  onChange = data => {
    this.setState({
      [data.type]: {
        ...this.state[data.type],
        value: data.value
      }
    });
  };

  render() {
    return (
      <section className="navigation">
        <Selection
          genre={this.state.genre}
          onGenreChange={this.onGenreChange}
        />

        <Slider data={this.state.year} onChange={this.onChange} />
        <Slider data={this.state.rating} onChange={this.onChange} />
        <Slider data={this.state.runtime} onChange={this.onChange} />

      </section>
    );
  }
}

export default Navigation;
\`\`\`



And here is the final code:
\`\`\`codeSandbox
n4opyj922l
\`\`\`

## Fetching Genres

Our sliders look great but, but we have only three genres to select from.

TMDB to the rescue!

We can get a list of genres from the database. But we have to fetch them first. Then we can save them in the \`Navigation\` component's state.

Here is the endpoint where we can get the genres from \`https://api.themoviedb.org/3/genre/movie/list?api_key=<<api_key>>&language=en-US\`.

Don't forget to replace the \`<<api_key>>\` with your own – use the evironmental variable, \`process.env.REACT_APP_TMDB_API_KEY\`.

Let's place \`fetch\` method in the \`componentDidMount\` of the \`Navigation\` component.

We should get data in the form of an array with the following structure:
\`\`\`plainJavascript
{
  genres: [
    { id: 28, name: "Action" },
    { id: 12, name: "Adventure"},
    ...
  ]
}
\`\`\`

Once we get the genres data, we can store them in the state:
\`\`\`plainJavascript
// Navigation.js

componentDidMount() {
  const genresURL = \`https://api.themoviedb.org/3/genre/movie/list?api_key=\${process.env.REACT_APP_TMDB_API_KEY}&language=en-US\`
  fetch(genresURL)
    .then(response => response.json())
    .then(data => this.setState({genres: data.genres }))
    .catch(error => console.log(error));
}
\`\`\`

Notice that we are storing genres in \`genres\`. We didn't have this property on the \`state\` before.
That's why we have to initialise it as an empty array.
\`\`\`plainJavascript
// Navigation.js

class Navigation extends React.Component {
  state = {
    genre: "Comedy",
    genres: [],
  }

  // rest of the component's code
}
\`\`\`

When the genres array gets stored in the \`state\` we can pass it to the \`Selection\` component as a new property \`genres\`.

\`\`\`plainJavascript
<Selection
  genres={this.state.genres}
  genre={this.state.genre}
  onGenreChange={this.onGenreChange}
/>
\`\`\`

Now, when the \`Selection\` component receives an array of \`genres\` it can iterate through it using a \`map\` method
and generate genre \`option\` elements on the fly.

\`\`\`plainJavascript
// Selection.js

import React from "react";
import "./Selection.css";

const Selection = ({ genres, genre, onGenreChange }) => (
  <div className="selection">
    <label>Genre</label>
    <select value={genre} onChange={onGenreChange}>
      {genres.map( genre => (
        <option key={genre.id} value={genre.name}>{genre.name}</option>
      ))}
    </select>
  </div>
)

export default Selection;
\`\`\`

Here is the final code:

\`\`\`codeSandbox
p7617xroqj
\`\`\`

`;



// WEBPACK FOOTER //
// ./src/book/chapters/app-navigation.js